import styled from '@emotion/styled';
import { uiTheme as t, Avatar, MetricsStrip, StatusIndicator, ActionMenu } from 'Components/ui';
import type { ActionMenuOption } from 'Components/ui';
import useTranslation from 'Hooks/useTranslation';
import type { EmployeeProfileI } from 'types';

interface Props {
  profile: EmployeeProfileI;
  /** Las arma la página según los permisos de quien mira. */
  actions: ActionMenuOption[];
  /** "1 de 4 obtenidas". Lo calcula la página, que es quien tiene los logros. */
  badgesLabel: string;
}

const Surface = styled.div({
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  boxShadow: t.shadow.card,
  overflow: 'hidden',
});

const Top = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 22,
  padding: '24px 26px',
  // Bajo el corte estrecho la fila se apila: el nombre y la línea de datos
  // necesitan el ancho completo antes que el avatar la altura.
  [`@media (max-width: ${t.breakpoint.sm})`]: {
    flexWrap: 'wrap',
    gap: 16,
    padding: '20px',
  },
});

const Identity = styled.div({ flex: 1, minWidth: 0 });

const NameRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
});

const Name = styled.span({
  fontFamily: t.font,
  fontSize: 26,
  lineHeight: 1.15,
  fontWeight: 700,
  letterSpacing: '-0.6px',
  color: t.color.title,
  [`@media (max-width: ${t.breakpoint.sm})`]: { fontSize: 21 },
});

const Facts = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexWrap: 'wrap',
  marginTop: 7,
  fontFamily: t.font,
  fontSize: 13,
  fontWeight: 500,
  color: t.color.text,
});

const Separator = styled.span({ color: t.color.border });

/**
 * Encabezado de la ficha del empleado.
 *
 * El número de nómina aparece solo en la franja de métricas: la propuesta de
 * diseño lo repetía también en la línea de datos, y es el mismo campo.
 *
 * La cuarta métrica es el rol. En el mock decía "terminal base" porque se
 * dibujó a partir de la pantalla anterior, que leía el arreglo del empleado con
 * los índices cruzados.
 *
 * No hay botón de editar: los datos vienen del sincronizado con TRESS y no hay
 * flujo de edición. Su lugar lo ocupa el menú de acciones de cuenta.
 */
const EmployeeHeaderCard = ({ profile, actions, badgesLabel }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const facts = [profile.jobTitle?.name, profile.department?.name, profile.terminal?.name].filter(Boolean);

  return (
    <Surface>
      <Top>
        <Avatar name={profile.fullName} size="lg" />
        <Identity>
          <NameRow>
            <Name>{profile.fullName}</Name>
            <StatusIndicator
              status={profile.status ? 'active' : 'inactive'}
              label={profile.status ? t2('Active') : t2('Inactive')}
            />
          </NameRow>
          <Facts>
            {facts.map((fact, index) => (
              <span key={fact}>
                {index > 0 ? <Separator>·</Separator> : null} {fact}
              </span>
            ))}
          </Facts>
        </Identity>
        <ActionMenu label={t2('employee_actions')} options={actions} />
      </Top>
      <MetricsStrip
        metrics={[
          { label: t2('EmployeeNumber'), value: profile.payrollNum ?? '—' },
          { label: t2('EntryDate'), value: profile.entryDate ?? '—' },
          { label: t2('UserType'), value: profile.role?.name ?? t2('no_role') },
          { label: t2('badges'), value: badgesLabel },
        ]}
      />
    </Surface>
  );
};

export default EmployeeHeaderCard;
